import type { ApiResponse, PageResult, PageQuery } from '../types';
import type { 
  AdminUser, Role, Menu, PermissionsVO, VisitLog, Supplier, SupplierService,
  Buyer, Product, MarketingForm, Order, OrderAuditDTO, 
  OrderFeeSettleBatchVO, FeePaymentAuditDTO, DictItem, DictItemDTO, DictBatchDTO, SupplierArchive} from '../types';
import type { Banner,BannerAddDTO,ProductIntroDTO, eCard } from '../types/banner';
// API 基础地址
const BASE = import.meta.env.VITE_API_BASE_URL || 'http://172.16.163.44:8080';
// API 基础地址
let API_BASE = ''
if(BASE.includes('https')){
  API_BASE = BASE + '/api'
}else{
  API_BASE = BASE 
}

// Token 存储 key（与新 API 统一）
const TOKEN_KEY = 'token';
const USER_TYPE_KEY = 'userType';

// ===================== Token 工具 =====================
const getToken = (): string | null => localStorage.getItem(TOKEN_KEY);
const getUserType = (): string | null => localStorage.getItem(USER_TYPE_KEY);

// ===================== 请求封装（带 Token + 401 处理）========================
async function request<T>(url: string, options?: RequestInit & { params?: Record<string, any> }): Promise<ApiResponse<T>> {
  const token = getToken();
  
  let finalUrl = `${API_BASE}${url}`;
  
  if (options?.params) {
    const query = new URLSearchParams();
    Object.entries(options.params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        query.append(key, String(value));
      }
    });
    if (query.toString()) {
      finalUrl += '?' + query.toString();
    }
  }
  
  const headers: Record<string, string> = {};
  if (!(options?.body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
  }
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  const { params, ...fetchOptions } = options || {};
  
  const response = await fetch(finalUrl, {
    headers,
    ...fetchOptions,
  });
  
  // 401 未登录或 Token 过期
  if (response.status === 401) {
    // 清除登录信息
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_TYPE_KEY);
    localStorage.removeItem('userInfo');
    localStorage.removeItem('adminInfo');
    
    // 跳转到登录页
    window.location.href = '/admin/login';
    throw new Error('未登录或登录已过期，请先登录');
  }
  
  // 403 无权限
  if (response.status === 403) {
    throw new Error('无权访问');
  }
  
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  
  // 根据 responseType 判断返回格式
  if ((fetchOptions as any).responseType === 'blob') {
    return response.blob() as unknown as ApiResponse<T>;
  }
  
  return response.json();
}

// ===================== 当前用户权限 API =====================
export const meApi = {
  getPermissions: () => request<PermissionsVO>('/me/permissions'),
};

// ===================== 角色管理 API =====================
export const roleApi = {
  getList: (params?: PageQuery & { roleName?: string; status?: number }) => {
    const query = new URLSearchParams();
    if (params?.pageNum) query.append('pageNum', params.pageNum.toString());
    if (params?.pageSize) query.append('pageSize', params.pageSize.toString());
    if (params?.roleName) query.append('roleName', params.roleName);
    if (params?.status !== undefined) query.append('status', params.status.toString());
    return request<PageResult<Role>>(`/admin/role/page?${query.toString()}`);
  },
  
  getAll: () => request<Role[]>('/admin/role/all'),
  
  getById: (id: number) => request<Role>(`/admin/role/${id}`),
  
  create: (data: Partial<Role>) => request<Role>('/admin/role', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  
  update: (id: number, data: Partial<Role>) => request<Role>(`/admin/role/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  
  delete: (id: number) => request<void>(`/admin/role/${id}`, { method: 'DELETE' }),
  
  deleteBatch: (ids: number[]) => request<void>(`/admin/role?ids=${ids.join(',')}`, { method: 'DELETE' }),
  
  updateStatus: (id: number, status: number) => request<void>(`/admin/role/${id}/status/${status}`, { method: 'PUT' }),
};

// ===================== 菜单管理 API =====================
export const menuApi = {
  getTree: () => request<Menu[]>('/admin/menu/tree'),
  
  getEnabledTree: () => request<Menu[]>('/admin/menu/tree/enabled'),
  
  getById: (id: number) => request<Menu>(`/admin/menu/${id}`),
  
  create: (data: Partial<Menu>) => request<Menu>('/admin/menu', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  
  update: (id: number, data: Partial<Menu>) => request<Menu>(`/admin/menu/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  
  delete: (id: number) => request<void>(`/admin/menu/${id}`, { method: 'DELETE' }),
  
  deleteBatch: (ids: number[]) => request<void>(`/admin/menu?ids=${ids.join(',')}`, { method: 'DELETE' }),
  
  updateStatus: (id: number, status: number) => request<void>(`/admin/menu/${id}/status/${status}`, { method: 'PUT' }),
};

// ===================== 供应商管理 API =====================
export const supplierApi = {
  getList: (params?: PageQuery & { companyName?: string; status?: number }) => {
    const query = new URLSearchParams();
    if (params?.pageNum) query.append('pageNum', params.pageNum.toString());
    if (params?.pageSize) query.append('pageSize', params.pageSize.toString());
    if (params?.companyName) query.append('companyName', params.companyName);
    if (params?.status !== undefined) query.append('status', params.status.toString());
    return request<PageResult<Supplier>>(`/admin/supplier/page?${query.toString()}`);
  },
  
  getById: (id: number) => request<Supplier>(`/admin/supplier/${id}`),
  
  create: (data: Partial<Supplier>) => request<Supplier>('/admin/supplier', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  
  update: (id: number, data: Partial<Supplier>) => request<Supplier>(`/admin/supplier/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  
  audit: (data: { id: number; status: number; remark?: string }) => request<void>('/admin/supplier/audit', {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  //获取供应商服务费配置
  getFeeConfig: (id: number) => request<SupplierService>(`/admin/supplier/${id}/fee-config`),
  //更新供应商服务费配置（不存在则创建）
  updateFeeConfig: (id: string, data: Partial<SupplierService>) => request<SupplierService>(`/admin/supplier/${id}/fee-config`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  
  delete: (id: number) => request<void>(`/admin/supplier/${id}`, { method: 'DELETE' }),
  
  deleteBatch: (ids: number[]) => request<void>(`/admin/supplier?ids=${ids.join(',')}`, { method: 'DELETE' }),
};

// ===================== 采购商管理 API =====================
export const buyerApi = {
  getList: (params?: PageQuery & { companyName?: string; status?: number }) => {
    const query = new URLSearchParams();
    if (params?.pageNum) query.append('pageNum', params.pageNum.toString());
    if (params?.pageSize) query.append('pageSize', params.pageSize.toString());
    if (params?.companyName) query.append('companyName', params.companyName);
    if (params?.status !== undefined) query.append('status', params.status.toString());
    return request<PageResult<Buyer>>(`/admin/buyer/page?${query.toString()}`);
  },
  
  getById: (id: number) => request<Buyer>(`/admin/buyer/${id}`),
  
  create: (data: Partial<Buyer>) => request<Buyer>('/admin/buyer', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  
  update: (id: number, data: Partial<Buyer>) => request<Buyer>(`/admin/buyer/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  
  audit: (data: { id: number; status: number; remark?: string }) => request<void>('/admin/buyer/audit', {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  
  delete: (id: number) => request<void>(`/admin/buyer/${id}`, { method: 'DELETE' }),
  
  deleteBatch: (ids: number[]) => request<void>(`/admin/buyer?ids=${ids.join(',')}`, { method: 'DELETE' }),
};

// ===================== 商品管理 API =====================
export const productApi = {
  getList: (params?: PageQuery & { productName?: string; category?: string; status?: number }) => {
    const query = new URLSearchParams();
    if (params?.pageNum) query.append('pageNum', params.pageNum.toString());
    if (params?.pageSize) query.append('pageSize', params.pageSize.toString());
    if (params?.productName) query.append('productName', params.productName);
    if (params?.category) query.append('category', params.category);
    if (params?.status !== undefined) query.append('status', params.status.toString());
    return request<PageResult<Product>>(`/admin/product/page?${query.toString()}`);
  },
  
  getById: (id: number) => request<Product>(`/admin/product/${id}`),
  
  create: (data: Partial<Product>) => request<Product>('/admin/product', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  
  update: (id: number, data: Partial<Product>) => request<Product>(`/admin/product/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  
  audit: (data: { id: number; status: number; remark?: string }) => request<void>('/admin/product/audit', {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  
  toggleStatus: (id: number, status: number) => request<void>(`/admin/product/${id}/status/${status}`, { method: 'PUT' }),
  
  delete: (id: number) => request<void>(`/admin/product/${id}`, { method: 'DELETE' }),
  
  deleteBatch: (ids: number[]) => request<void>(`/admin/product?ids=${ids.join(',')}`, { method: 'DELETE' }),
  
  getCategories: () => request<string[]>('/admin/product/categories'),
};

// ===================== 营销表单 API =====================
export const marketingFormApi = {
  getList: (params?: PageQuery & { formType?: string; contactName?: string; status?: number }) => {
    const query = new URLSearchParams();
    if (params?.pageNum) query.append('pageNum', params.pageNum.toString());
    if (params?.pageSize) query.append('pageSize', params.pageSize.toString());
    if (params?.formType) query.append('formType', params.formType);
    if (params?.contactName) query.append('contactName', params.contactName);
    if (params?.status !== undefined) query.append('status', params.status.toString());
    return request<PageResult<MarketingForm>>(`/admin/marketing-form/page?${query.toString()}`);
  },
  
  getById: (id: number) => request<MarketingForm>(`/admin/marketing-form/${id}`),
  
  handle: (id: number, status: number, handleRemark?: string, handlerId?: number, handlerName?: string) => {
    const query = new URLSearchParams();
    query.append('status', status.toString());
    if (handleRemark) query.append('handleRemark', handleRemark);
    if (handlerId) query.append('handlerId', handlerId.toString());
    if (handlerName) query.append('handlerName', handlerName);
    return request<void>(`/admin/marketing-form/${id}/handle?${query.toString()}`, { method: 'PUT' });
  },
  
  delete: (id: number) => request<void>(`/admin/marketing-form/${id}`, { method: 'DELETE' }),
  
  deleteBatch: (ids: number[]) => request<void>(`/admin/marketing-form?ids=${ids.join(',')}`, { method: 'DELETE' }),
  
  getTypes: () => request<string[]>('/admin/marketing-form/types'),
  
  countPending: () => request<number>('/admin/marketing-form/count-pending'),
};

// ===================== 用户管理 API =====================
export const userApi = {
  getList: (params?: PageQuery & { username?: string; nickname?: string; phone?: string; status?: number }) => {
    const query = new URLSearchParams();
    if (params?.pageNum) query.append('page', params.pageNum.toString());
    if (params?.pageSize) query.append('size', params.pageSize.toString());
    if (params?.username) query.append('username', params.username);
    if (params?.nickname) query.append('nickname', params.nickname);
    if (params?.phone) query.append('phone', params.phone);
    if (params?.status !== undefined) query.append('status', params.status.toString());
    return request<PageResult<AdminUser>>(`/admin/user/page?${query.toString()}`);
  },
  
  getById: (id: number) => request<AdminUser>(`/admin/user/${id}`),
  
  create: (data: Partial<AdminUser> & { password?: string; roleIds?: number[] }) => request<AdminUser>('/admin/user', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  
  update: (id: number, data: Partial<AdminUser> & { roleIds?: number[] }) => request<AdminUser>(`/admin/user/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  
  delete: (id: number) => request<void>(`/admin/user/${id}`, { method: 'DELETE' }),
  
  deleteBatch: (ids: number[]) => request<void>(`/admin/user?ids=${ids.join(',')}`, { method: 'DELETE' }),
  
  updateStatus: (id: number, status: number) => request<void>(`/admin/user/${id}/status/${status}`, { method: 'PUT' }),
  
  resetPassword: (id: number, password: string) => request<void>(`/admin/user/${id}/password`, {
    method: 'PUT',
    body: JSON.stringify({ password }),
  }),
  
  getRoles: () => request<Role[]>('/admin/role/all'),
};

// ===================== 订单审核 API =====================
export const orderApi = {
  getPageList: (params?: { pageNum?: number; pageSize?: number; status?: number; orderNo?: string }) => {
    const query = new URLSearchParams();
    if (params?.pageNum) query.append('pageNum', params.pageNum.toString());
    if (params?.pageSize) query.append('pageSize', params.pageSize.toString());
    if (params?.status !== undefined && params.status !== null) query.append('status', params.status.toString());
    if (params?.orderNo) query.append('orderNo', params.orderNo);
    return request<PageResult<Order>>(`/admin/order/page?${query.toString()}`);
  },

  audit: (data: OrderAuditDTO) => request<void>('/admin/order/audit', {
    method: 'PUT',
    body: JSON.stringify(data),
  }),

  // 查询订单关联的结算批次列表
  getFeePayments: (orderId: number) =>
    request<OrderFeeSettleBatchVO[]>(`/admin/order-fee-payment/order/${orderId}`),

  // 审核服务费凭证
  auditFeePayment: (data: FeePaymentAuditDTO) =>
    request<void>('/admin/order-fee-payment/audit', {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  // 分页查询结算批次列表
  getFeePaymentPage: (params?: {
    pageNum?: number;
    pageSize?: number;
    orderId?: number;
    payerType?: number;
    status?: number;
  }) => {
    const query = new URLSearchParams();
    if (params?.pageNum) query.append('pageNum', params.pageNum.toString());
    if (params?.pageSize) query.append('pageSize', params.pageSize.toString());
    if (params?.orderId !== undefined) query.append('orderId', params.orderId.toString());
    if (params?.payerType !== undefined) query.append('payerType', params.payerType.toString());
    if (params?.status !== undefined) query.append('status', params.status.toString());
    return request<PageResult<OrderFeeSettleBatchVO>>(`/admin/order-fee-payment/page?${query.toString()}`);
  },

  // 调整单条订单服务费
  updateOrderFee: (orderId: number, data: {
    platformFee?: number;
    buyerFee?: number;
    supplierFee?: number;
    buyerPayAmount?: number;
    supplierReceiveAmount?: number;
    remark?: string;
  }) => request<void>(`/admin/order/${orderId}/fee`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),

  // 按比例批量调整订单服务费
  batchUpdateOrderFee: (data: {
    orderIds: number[];
    platformFeeRate?: number;
    buyerFeeRate?: number;
    supplierFeeRate?: number;
    remark?: string;
  }) => request<void>('/admin/order/fee/batch', {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
};

// ===================== 文章管理 API（复用主 API）========================
export { articleApi } from '@/user/api';

// ===================== 系统字典 API =====================
export interface SysDictVO {
  id: number;
  dictKey: string;
  dictValue: string;
  valueType: string;
  dictGroup: string;
  name: string;
  description: string;
  editable: number;
  sort: number;
  updateTime: string;
}

// ===================== 访问日志 API =====================
export const visitLogApi = {
  getList: (params?: PageQuery & { ip?: string; username?: string; url?: string; startTime?: string; endTime?: string }) => {
    const query = new URLSearchParams();
    if (params?.pageNum) query.append('pageNum', params.pageNum.toString());
    if (params?.pageSize) query.append('pageSize', params.pageSize.toString());
    if (params?.ip) query.append('ip', params.ip);
    if (params?.username) query.append('username', params.username);
    if (params?.url) query.append('url', params.url);
    if (params?.startTime) query.append('startTime', params.startTime);
    if (params?.endTime) query.append('endTime', params.endTime);
    return request<PageResult<VisitLog>>(`/admin/access-log/page?${query.toString()}`);
  },
};

// ===================== 验证码 API（复用主 API）========================
export { captchaApi } from '@/user/api';

// ===================== 轮播图管理 API =====================
export const bannerApi = {
  // 分页查询轮播图列表
  getPageList: (params?: { pageNum?: number; pageSize?: number; status?: number; type?: string }) => {
    const query = new URLSearchParams();
    if (params?.pageNum) query.append('pageNum', params.pageNum.toString());
    if (params?.pageSize) query.append('pageSize', params.pageSize.toString());
    if (params?.status !== undefined && params.status !== null) query.append('status', params.status.toString());
    if (params?.type) query.append('type', params.type);
    return request<PageResult<Banner>>(`/admin/banner/page?${query.toString()}`);
  },

  // 根据ID查询轮播图
  getById: (id: number) => request<Banner>(`/admin/banner/${id}`),

  // 创建轮播图
  create: (data: BannerAddDTO) => request<Banner>('/admin/banner', {
    method: 'POST',
    body: JSON.stringify(data),
  }),

  // 更新轮播图
  update: (id: number, data: Partial<Banner>) => request<Banner>(`/admin/banner/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),

  // 删除轮播图
  delete: (id: number) => request<void>(`/admin/banner/${id}`, { method: 'DELETE' }),

  // 批量删除轮播图
  deleteBatch: (ids: number[]) => request<void>(`/admin/banner?ids=${ids.join(',')}`, { method: 'DELETE' }),
};

// ===================== 产品介绍管理 API =====================
export const productIntroApi = {
  // 分页查询产品介绍列表
  getPageList: (params?: { pageNum?: number; pageSize?: number; status?: number; title?: string }) => {
    const query = new URLSearchParams();
    if (params?.pageNum) query.append('pageNum', params.pageNum.toString());
    if (params?.pageSize) query.append('pageSize', params.pageSize.toString());
    if (params?.status !== undefined && params.status !== null) query.append('status', params.status.toString());
    if (params?.title) query.append('title', params.title);
    return request<PageResult<ProductIntroDTO>>(`/admin/product-intro/page?${query.toString()}`);
  },

  // 根据ID查询产品介绍
  getById: (id: number) => request<ProductIntroDTO>(`/admin/product-intro/${id}`),

  // 创建产品介绍
  create: (data: ProductIntroDTO) => request<ProductIntroDTO>('/admin/product-intro', {
    method: 'POST',
    body: JSON.stringify(data),
  }),

  // 更新产品介绍
  update: (id: number, data: Partial<ProductIntroDTO>) => request<ProductIntroDTO>(`/admin/product-intro/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),

  // 删除产品介绍
  delete: (id: number) => request<void>(`/admin/product-intro/${id}`, { method: 'DELETE' }),

  // 批量删除产品介绍
  deleteBatch: (ids: number[]) => request<void>(`/admin/product-intro?ids=${ids.join(',')}`, { method: 'DELETE' }),
};

// ===================== 电子名片管理 API =====================
export const eCardApi = {
  // 分页查询产品介绍列表
  getPageList: (params?: { pageNum?: number; pageSize?: number; status?: number}) => {
    const query = new URLSearchParams();
    if (params?.pageNum) query.append('pageNum', params.pageNum.toString());
    if (params?.pageSize) query.append('pageSize', params.pageSize.toString());
    if (params?.status !== undefined && params.status !== null) query.append('status', params.status.toString());
    return request<PageResult<eCard>>(`/admin/business-card/page?${query.toString()}`);
  },
  // 删除产品介绍
  delete: (id: number) => request<void>(`/admin/business-card/${id}`, { method: 'DELETE' }),
}

// ===================== 数据字典管理 API =====================
export const dictApi = {
  // 创建字典项
  create: (data: DictItemDTO) => request<DictItem>('/admin/dict', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  // 按 key 查询字典项
  getByKey: (dictKey: string) => request<DictItem>(`/admin/dict/${dictKey}`),
  // 按 key 修改字典值
  updateByKey: (dictKey: string, data: Partial<DictItemDTO>) => request<DictItem>(`/admin/dict/${dictKey}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  // 删除字典项
  delete: (id: number) => request<void>(`/admin/dict/${id}`, { method: 'DELETE' }),
  // 批量按 key 修改字典值
  updateBatch: (data: any) => request<void>('/admin/dict/batch', {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  // 按分组查询字典项
  getByGroup: (dictGroup: string) => request<DictItem[]>(`/admin/dict/group/${dictGroup}`),
  // 分页查询字典项
  getPageList: (params?: { pageNum?: number; pageSize?: number; keyword?: string; }) => {
    const query = new URLSearchParams();
    if (params?.pageNum) query.append('pageNum', params.pageNum.toString());
    if (params?.pageSize) query.append('pageSize', params.pageSize.toString());
    if (params?.keyword) query.append('keyword', params.keyword);
    return request<PageResult<DictItem>>(`/admin/dict/page?${query.toString()}`);
  },
};
// ===================== 供应商档案管理 API =====================
export const supplierArchiveApi = {
  // 创建供应商档案
  create: (data: Partial<SupplierArchive>) => request<SupplierArchive>('/admin/supplier-archive', {
    method: 'POST',
    body: JSON.stringify(data),
  }),

  // 批量删除供应商档案
  deleteBatch: (ids: number[]) => request<void>(`/admin/supplier-archive?ids=${ids.join(',')}`, { method: 'DELETE' }),

  // 根据ID查询供应商档案
  getById: (id: number) => request<SupplierArchive>(`/admin/supplier-archive/${id}`),

  // 更新供应商档案
  update: (id: number, data: Partial<SupplierArchive>) => request<SupplierArchive>(`/admin/supplier-archive/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),

  // 删除供应商档案
  delete: (id: number) => request<void>(`/admin/supplier-archive/${id}`, { method: 'DELETE' }),

  // 更新供应商档案状态
  updateStatus: (id: number, status: string) => request<void>(`/admin/supplier-archive/${id}/status/${status}`, { method: 'PUT' }),

  // 分页查询供应商档案列表
  getPageList: (params?: { pageNum?: number; pageSize?: number; resourceLibraryName?: string; supplierName?: string; status?: string;mainBusinessScope?: string }) => {
    const query = new URLSearchParams();
    if (params?.pageNum) query.append('pageNum', params.pageNum.toString());
    if (params?.pageSize) query.append('pageSize', params.pageSize.toString());
    if (params?.resourceLibraryName) query.append('resourceLibraryName', params.resourceLibraryName);
    if (params?.supplierName) query.append('supplierName', params.supplierName);
    if (params?.status) query.append('status', params.status);
    if (params?.mainBusinessScope) query.append('mainBusinessScope', params.mainBusinessScope);
    return request<PageResult<SupplierArchive>>(`/admin/supplier-archive/page?${query.toString()}`);
  },
  //导入
  importSupplierArchive: (formData: FormData) => request<any>('/admin/supplier-archive/import', {
    method: 'POST',
    body: formData,
  }),
  // 供应商档案预警提醒
  warningReminder: () => request<any>(`/admin/supplier-archive/warning/reminder`, { 
    method: 'GET' 
  }),
  //导出供应商档案
  exportSupplierArchive: async (params?: { resourceLibraryName?: string; status?: number; supplierName?: string }) => {
    const token = getToken();
    let url = `${API_BASE}/admin/supplier-archive/export`;
    
    if (params) {
      const query = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          query.append(key, String(value));
        }
      });
      if (query.toString()) {
        url += '?' + query.toString();
      }
    }
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        ...(token && { 'Authorization': `Bearer ${token}` }),
      },
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return response.blob();
  },
};
